/**
 * Definiciones centralizadas de KPIs financieros para Habitia.
 *
 * Este archivo es la fuente de verdad para:
 * - Tooltips explicativos en la UI (KpiInfoTooltip)
 * - Logica de colores/thresholds de cada KPI
 * - Metadata reutilizable para reportes exportables
 */

import { colors } from "./designTokens";

// ─── Definiciones ───────────────────────────────────────────────

export const KPI_DEFINITIONS = {
  tasa_morosidad_unidades: {
    id: "tasa_morosidad_unidades",
    name: "Tasa de Morosidad",
    emoji: "\ud83d\udcca",
    unit: "%",
    description:
      "El porcentaje de unidades del edificio que tienen al menos una cuota de gastos comunes vencida e impaga.",
    formula:
      "Unidades con deuda vencida \u00f7 Total de unidades \u00d7 100",
    howToRead:
      "Mide la amplitud de la morosidad: cu\u00e1ntas unidades est\u00e1n en falta, sin importar el monto.",
    thresholds: [
      { max: 15, label: "Saludable", status: "success" },
      { max: 25, label: "Normal para el mercado", status: "warning" },
      { max: Infinity, label: "Requiere acci\u00f3n urgente", status: "error" },
    ],
    direction: "lower_is_better",
    format: "percentage",
  },

  tasa_morosidad_monto: {
    id: "tasa_morosidad_monto",
    name: "Morosidad por Monto",
    emoji: "\ud83d\udcb0",
    unit: "%",
    description:
      "Qu\u00e9 porcentaje del dinero que deber\u00eda haberse cobrado est\u00e1 efectivamente en mora.",
    formula:
      "Total adeudado vencido \u00f7 Total liquidado en el per\u00edodo \u00d7 100",
    howToRead:
      "Complementa la tasa por unidades: un edificio puede tener pocas unidades morosas pero con montos altos, o muchas con montos bajos.",
    thresholds: [
      { max: 10, label: "Saludable", status: "success" },
      { max: 20, label: "Atenci\u00f3n moderada", status: "warning" },
      { max: Infinity, label: "Impacto significativo en caja", status: "error" },
    ],
    direction: "lower_is_better",
    format: "percentage",
  },

  aging_deuda: {
    id: "aging_deuda",
    name: "Antig\u00fcedad de Deuda",
    emoji: "\ud83d\udcc5",
    unit: "",
    description:
      "C\u00f3mo se distribuye la deuda del edificio seg\u00fan cu\u00e1nto tiempo lleva sin pagarse.",
    formula:
      "Cada unidad morosa se clasifica seg\u00fan su cuota impaga m\u00e1s antigua en rangos de 30, 60, 90, 180 y +180 d\u00edas.",
    howToRead:
      "Permite priorizar la gesti\u00f3n de cobranza: deuda reciente es recuperable, deuda antigua puede requerir acci\u00f3n legal.",
    thresholds: [
      { max: 60, label: "Atrasos normales y recuperables", status: "success" },
      { max: 90, label: "La cobranza necesita refuerzo", status: "warning" },
      { max: Infinity, label: "Alto riesgo de incobrabilidad", status: "error" },
    ],
    direction: "lower_is_better",
    format: "days",
  },

  cei: {
    id: "cei",
    name: "\u00cdndice de Efectividad de Cobranza (CEI)",
    emoji: "\ud83c\udfaf",
    unit: "%",
    description:
      "Qu\u00e9 tan eficiente es la administraci\u00f3n cobrando lo que se puede cobrar en un per\u00edodo dado.",
    formula:
      "Compara lo que efectivamente se cobr\u00f3 contra lo que estaba disponible para cobrar (excluyendo lo que a\u00fan no venci\u00f3). Cuanto m\u00e1s cerca de 100%, mejor.",
    howToRead:
      "Es superior a la tasa de recaudaci\u00f3n simple porque a\u00edsla lo efectivamente cobrable de lo que a\u00fan no venci\u00f3.",
    thresholds: [
      { max: 85, label: "Se necesitan mejoras en cobranza", status: "error" },
      { max: 95, label: "Buena gesti\u00f3n", status: "warning" },
      { max: Infinity, label: "Excelente gesti\u00f3n de cobranza", status: "success" },
    ],
    direction: "higher_is_better",
    format: "percentage",
  },

  concentracion_mora: {
    id: "concentracion_mora",
    name: "Concentraci\u00f3n de Mora",
    emoji: "\ud83d\udd0d",
    unit: "%",
    description:
      "Si la deuda del edificio est\u00e1 repartida entre muchas unidades o concentrada en pocas.",
    formula:
      "Se analiza qu\u00e9 porcentaje de la deuda total corresponde al 20% de los mayores deudores (an\u00e1lisis Pareto).",
    howToRead:
      "Es m\u00e1s f\u00e1cil de resolver cuando se concentra en pocos casos (gesti\u00f3n focalizada) que cuando est\u00e1 distribuida (problema sist\u00e9mico).",
    thresholds: [
      { max: 50, label: "Mora distribuida, problema sist\u00e9mico", status: "warning" },
      { max: 80, label: "Concentraci\u00f3n moderada", status: "success" },
      { max: Infinity, label: "Pocos grandes deudores, gesti\u00f3n focalizada", status: "success" },
    ],
    direction: "higher_is_better",
    format: "percentage",
  },

  ratio_gastos_operativos: {
    id: "ratio_gastos_operativos",
    name: "Ratio de Gastos Operativos",
    emoji: "\ud83d\udcb5",
    unit: "%",
    description:
      "Qu\u00e9 porcentaje de los ingresos del edificio se consume en gastos de operaci\u00f3n.",
    formula:
      "Gastos operativos totales \u00f7 Ingresos totales \u00d7 100",
    howToRead:
      "En edificios con personal de porter\u00eda, este ratio naturalmente es m\u00e1s alto que el benchmark internacional de 40%.",
    thresholds: [
      { max: 70, label: "Eficiente para PH con personal", status: "success" },
      { max: 85, label: "Normal pero ajustado", status: "warning" },
      { max: Infinity, label: "Margen muy estrecho, riesgo financiero", status: "error" },
    ],
    direction: "lower_is_better",
    format: "percentage",
  },

  desvio_presupuestal: {
    id: "desvio_presupuestal",
    name: "Desv\u00edo Presupuestal",
    emoji: "\ud83d\udccb",
    unit: "%",
    description:
      "Cu\u00e1nto se desv\u00eda el gasto real respecto a lo que se aprob\u00f3 en asamblea.",
    formula:
      "(Gasto real - Presupuesto aprobado) \u00f7 Presupuesto aprobado \u00d7 100",
    howToRead:
      "Un valor negativo significa que se gast\u00f3 menos de lo presupuestado. Un valor positivo alto requiere explicaci\u00f3n en asamblea.",
    thresholds: [
      { max: 5, label: "Gesti\u00f3n alineada al presupuesto", status: "success" },
      { max: 15, label: "Desv\u00edo moderado, revisar rubros", status: "warning" },
      { max: Infinity, label: "Desv\u00edo significativo, requiere explicaci\u00f3n", status: "error" },
    ],
    direction: "lower_is_better",
    format: "percentage_signed",
  },

  meses_reserva: {
    id: "meses_reserva",
    name: "Meses de Reserva",
    emoji: "\ud83c\udfe6",
    unit: "meses",
    description:
      "Cu\u00e1ntos meses podr\u00eda funcionar el edificio solo con el fondo de reserva si dejara de recaudar gastos comunes.",
    formula:
      "Saldo del fondo de reserva \u00f7 Gasto mensual promedio de los \u00faltimos 3 meses.",
    howToRead:
      "Es el colch\u00f3n financiero del edificio ante imprevistos o ca\u00eddas en la recaudaci\u00f3n.",
    thresholds: [
      { max: 1, label: "Situaci\u00f3n cr\u00edtica, sin colch\u00f3n financiero", status: "error" },
      { max: 2, label: "Reserva m\u00ednima", status: "warning" },
      { max: Infinity, label: "Reserva s\u00f3lida", status: "success" },
    ],
    direction: "higher_is_better",
    format: "number",
  },
};

// ─── Buckets de aging ───────────────────────────────────────────

export const AGING_BUCKETS = [
  { key: "0_30", label: "0-30 d\u00edas", min: 0, max: 30, color: colors.success, colorLight: colors.successLight },
  { key: "31_60", label: "31-60 d\u00edas", min: 31, max: 60, color: colors.info, colorLight: colors.infoLight },
  { key: "61_90", label: "61-90 d\u00edas", min: 61, max: 90, color: colors.warning, colorLight: colors.warningLight },
  { key: "91_180", label: "91-180 d\u00edas", min: 91, max: 180, color: colors.error, colorLight: colors.errorLight },
  { key: "180_plus", label: "180+ d\u00edas", min: 181, max: Infinity, color: colors.errorDark, colorLight: colors.errorLight },
];

// ─── Clasificacion de deudores ──────────────────────────────────

export const DEBTOR_CLASSIFICATIONS = {
  circunstancial: {
    key: "circunstancial",
    label: "Circunstancial",
    description: "1-2 meses de atraso, historial limpio",
  },
  recurrente: {
    key: "recurrente",
    label: "Recurrente",
    description: "Patr\u00f3n de atrasos en 3+ de los \u00faltimos 6 meses",
  },
  estructural: {
    key: "estructural",
    label: "Estructural",
    description: "6+ meses consecutivos de mora",
  },
};

// ─── Helpers ────────────────────────────────────────────────────

/**
 * Devuelve el threshold activo para un valor dado de un KPI.
 * @param {string} kpiKey — clave en KPI_DEFINITIONS
 * @param {number} value — valor actual del KPI
 * @returns {{ label: string, status: "success"|"warning"|"error" } | null}
 */
export function getKpiStatus(kpiKey, value) {
  const def = KPI_DEFINITIONS[kpiKey];
  if (!def || value == null || isNaN(value)) return null;

  const absValue = def.format === "percentage_signed" ? Math.abs(value) : value;

  for (const t of def.thresholds) {
    if (absValue <= t.max) {
      return { label: t.label, status: t.status };
    }
  }
  return null;
}

/**
 * Devuelve el string de color de badge para usar con KPICard.badgeColor.
 * @param {string} kpiKey
 * @param {number} value
 * @returns {"success"|"warning"|"error"}
 */
export function getKpiBadgeColor(kpiKey, value) {
  const result = getKpiStatus(kpiKey, value);
  return result ? result.status : "success";
}

/**
 * Devuelve el color hex del status (para estilos inline).
 * @param {"success"|"warning"|"error"} status
 * @returns {string}
 */
export function getStatusColor(status) {
  const map = {
    success: colors.success,
    warning: colors.warning,
    error: colors.error,
  };
  return map[status] || colors.gray600;
}

/**
 * Devuelve el color hex de fondo claro del status.
 * @param {"success"|"warning"|"error"} status
 * @returns {string}
 */
export function getStatusColorLight(status) {
  const map = {
    success: colors.successLight,
    warning: colors.warningLight,
    error: colors.errorLight,
  };
  return map[status] || colors.gray100;
}
